'use client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Option } from '@/components/ui/multi-select';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useCreateQuestion } from '@/hooks/useCreateQuestion';
import { useSetupProfile } from '@/hooks/useSetupProfile';
import { errorHandler } from '@/lib/errorHandler';
import { cn, getPlainTextLength } from '@/lib/utils';
import {
  CreateQuestionRequest,
  CreateQuestionResponse,
  QuestionInfoType,
} from '@/types/api/question';
import { zodResolver } from '@hookform/resolvers/zod';
import Image from 'next/image';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { z } from 'zod';

const formSchema = z.object({
  question: z
    .string()
    .trim()
    .min(1, {
      message: 'Question is required',
    })
    .max(200, {
      message: 'Question must be less than 200 characters',
    }),

  category: z
    .string({
      required_error: 'Category is required',
    })
    .min(1, {
      message: 'Category is required',
    }),
  discussionCategoryId: z.string().optional(),
  isAnonymous: z.boolean().optional(),
  description: z
    .string()
    .trim()
    .refine(
      (value) => {
        if (!value) return true; // Allow empty values since it's optional
        const plainTextLength = getPlainTextLength(value);
        return plainTextLength <= 1500;
      },
      {
        message: 'Description must be less than 1500 characters',
      }
    )
    .optional(),
});

interface AskQuestionFormProps {
  isModal?: boolean;
  discussionCategoryId?: string;
  onSuccess?: (data?: CreateQuestionResponse['data'] | null) => void;
  isEdit?: boolean;
  defaultQuestionInfo?: QuestionInfoType;
  onUpdateSuccessCallback?: (state: boolean) => void;
  isDiscussion?: boolean;
  onFormDirtyChange?: (isDirty: boolean) => void;
}

export const AskQuestionForm = ({
  isModal,
  discussionCategoryId,
  onSuccess,
  isEdit,
  defaultQuestionInfo,
  onUpdateSuccessCallback,
  isDiscussion,
  onFormDirtyChange,
}: AskQuestionFormProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const questionType = pathname.split('/')[1];
  const { getCategoriesApi } = useSetupProfile({
    loadCategories: true,
    filterType:
      questionType === 'idea-starters' ? 'IDEA_STARTER' : 'AREA_OF_INTEREST',
  });
  const { getCategoriesApi: getDiscussionCategoriesApi } = useSetupProfile({
    loadCategories: isDiscussion,
    filterType: 'DISCUSSIONS',
  });

  const discussionCategoryOptions = useMemo(() => {
    const grouped = Object.entries(
      getDiscussionCategoriesApi.data?.data || {}
    ).flatMap(([key, value]) =>
      value.map((item) => ({
        value: item.id,
        label: item.name,
        description: item.description,
      }))
    );

    return grouped ?? [];
  }, [getDiscussionCategoriesApi.data]);

  // Find the "Other" category to use as default
  const discussionOtherCategory = useMemo(() => {
    return discussionCategoryOptions.find(
      (category) => category.label === 'Other'
    );
  }, [discussionCategoryOptions]);

  const areaOfInterestOptions: Option[] = useMemo(() => {
    const grouped = Object.entries(getCategoriesApi.data?.data || {}).flatMap(
      ([key, value]) =>
        value.map((item) => ({ value: item.id, label: item.name, group: key }))
    );

    return grouped ?? [];
  }, [getCategoriesApi.data]);

  // Find the "Other" category to use as default
  const otherCategory = useMemo(() => {
    return areaOfInterestOptions.find((category) => category.label === 'Other');
  }, [areaOfInterestOptions, discussionOtherCategory, pathname]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      question: defaultQuestionInfo?.questionTitle || '',
      category:
        defaultQuestionInfo?.category?.id ||
        (isDiscussion ? otherCategory?.value : ''),
      isAnonymous: defaultQuestionInfo?.isAnonymous || false,
      description: defaultQuestionInfo?.questionDescription || '',
      discussionCategoryId:
        discussionCategoryId || discussionOtherCategory?.value || undefined,
    },
  });
  useEffect(() => {
    if (!discussionCategoryId && discussionOtherCategory?.value) {
      form.setValue('discussionCategoryId', discussionOtherCategory.value);
    }
  }, [discussionOtherCategory]);
  useEffect(() => {
    if (
      !defaultQuestionInfo?.category?.id &&
      isDiscussion &&
      otherCategory?.value
    ) {
      form.setValue('category', otherCategory.value);
    }
  }, [otherCategory]);

  // Notify parent component when form dirty state changes
  useEffect(() => {
    onFormDirtyChange?.(form.formState.isDirty);
  }, [form.formState.isDirty, onFormDirtyChange]);

  const { createQuestionApi, updateQuestionApi } = useCreateQuestion();

  const { mutate: askQuestionApi, isPending: isLoading } = createQuestionApi;

  function onSubmit(values: z.infer<typeof formSchema>) {
    const payload: CreateQuestionRequest = {
      ...values,
      categoryId: values.category,
      questionTitle: values.question,
      questionDescription: values.description,
      isAnonymous: values.isAnonymous,
      discussionCategoryId: values.discussionCategoryId,
      questionType:
        isEdit && defaultQuestionInfo?.questionType
          ? defaultQuestionInfo.questionType
          : isDiscussion || discussionCategoryId
            ? 'DISCUSSIONS'
            : 'NORMAL',
    };

    if (isEdit && defaultQuestionInfo?.id) {
      updateQuestionApi.mutate(
        {
          ...payload,
          id: defaultQuestionInfo?.id || '',
        },
        {
          onSuccess: ({ data }) => {
            toast.success('Question updated successfully');
            form.reset();
            onUpdateSuccessCallback?.(true);
          },
          onError: (error) => {
            toast.error(errorHandler(error));
          },
        }
      );
      return;
    } else {
      askQuestionApi(payload, {
        onSuccess: ({ data }) => {
          toast.success('Question posted successfully');
          if (data.slug && !isModal) router.push(`/home/question/${data.slug}`);
          form.reset();
          onSuccess?.(data);
        },
        onError: (error) => {
          toast.error(errorHandler(error));
        },
      });
    }
  }

  const param = useSearchParams();
  const categoryTitle = param.get('title') || '';

  const isTitleVisible = !!isModal && !!categoryTitle;

  return (
    <Card
      className={cn(
        'shadow-lg rounded-4xl',

        isModal && 'shadow-none border-none'
      )}
    >
      <CardContent className={cn(isModal && 'p-0')}>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            {isTitleVisible && (
              <div className="flex gap-2.5 bg-[#F4F5F7] rounded-md p-2 items-center">
                <Image
                  src="/story-banner.svg"
                  width={20}
                  height={20}
                  alt="story-banner"
                  className="w-4 h-4"
                />

                <h4 className="font-bold text-base">{categoryTitle}</h4>
              </div>
            )}

            <FormField
              control={form.control}
              name="question"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#24292E]">
                    Enter Question
                    <sup className="w-3 h-2 -left-2 top-0.5  text-red-500 font-bold">
                      *
                    </sup>
                  </FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="off"
                      placeholder="E.g., How can leaders drive innovation in remote teams?"
                      className="p-6"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                  <span
                    className={cn(
                      'text-sm text-gray-500 text-right',
                      field.value?.trim()?.length &&
                        field.value?.trim()?.length > 200 &&
                        'text-red-500'
                    )}
                  >
                    {field.value?.trim()?.length || 0}/200 characters
                  </span>
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4 w-full ">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Type/Category
                      <sup className="w-3 h-2 -left-2 top-0.5 text-red-500 font-bold">
                        *
                      </sup>
                    </FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      {...field}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full truncate">
                          <SelectValue placeholder="Choose category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {areaOfInterestOptions?.map((item) => (
                          <SelectItem key={item.value} value={item.value}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {isDiscussion && (
                <FormField
                  control={form.control}
                  name="discussionCategoryId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Discussion Category
                        <sup className="w-3 h-2 -left-2 top-0.5 text-red-500 font-bold">
                          *
                        </sup>
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        {...field}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full truncate">
                            <SelectValue placeholder="Choose discussion category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {discussionCategoryOptions?.map((item) => (
                            <SelectItem key={item.value} value={item.value}>
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="isAnonymous"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="opacity-0">Anonymously</FormLabel>
                    <div className="w-full flex items-center justify-between bg-ihs-accent-blue border border-ihs-blue/25 p-4 rounded-full">
                      <FormLabel>Post Anonymously</FormLabel>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </div>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#24292E]">Description</FormLabel>
                  <FormControl>
                    <RichTextEditor
                      value={field.value || ''}
                      onChange={field.onChange}
                      placeholder="Enter background or context of question here..."
                      limit={1500}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="justify-end flex">
              <Button
                isLoading={isLoading || updateQuestionApi.isPending}
                className="font-bold uppercase py-6 cursor-pointer"
                size="lg"
                type="submit"
              >
                {isEdit ? 'Update Question' : 'Post Question'}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
